import { createSlice } from "@reduxjs/toolkit";
import {
  createEvidenceThunk,
  getEvidenceThunk,
  listEvidenceThunk,
  updateEvidenceThunk,
  deleteEvidenceThunk,
  flagEvidenceThunk,
  getEvidenceFlagsThunk,
  resolveFlagThunk,
  toggleHelpfulThunk,
  checkHelpfulThunk,
  getPostScoreThunk,
  getUserEvidenceStatsThunk,
  getPostEvidenceSummaryThunk,
  getDisputedEvidenceThunk,
  adminUpdateEvidenceStatusThunk,
} from "./postEvidenceActions";

const initialState = {
  byId: {},            // evidenceId -> evidence object
  byPost: {},          // postId -> [evidence list]
  flagsByEvidence: {}, // evidenceId -> array of flags
  helpful: {},         // evidenceId -> { hasVoted, count }
  postScores: {},      // postId -> score
  userStats: {},       // userId -> stats
  summaries: {},       // postId -> summary structure
  disputed: [],        // admin queue
  loading: false,
  error: null
};

const slice = createSlice({
  name: "postEvidence",
  initialState,
  reducers: {
    resetEvidenceState: (state) => {
      return initialState;
    }
  },
  extraReducers: (builder) => {

    // CREATE
    builder
      .addCase(createEvidenceThunk.pending, (s) => { s.loading = true; s.error = null; })
      .addCase(createEvidenceThunk.fulfilled, (s, { payload }) => {
        s.loading = false;
        s.byId[payload.id] = payload;
        const pid = payload.post_id;
        if (!s.byPost[pid]) s.byPost[pid] = [];
        s.byPost[pid].push(payload);
      })
      .addCase(createEvidenceThunk.rejected, (s, { payload }) => { s.loading = false; s.error = payload; });

    // GET one
    builder
      .addCase(getEvidenceThunk.pending, (s) => { s.loading = true; s.error = null; })
      .addCase(getEvidenceThunk.fulfilled, (s, { payload }) => {
        s.loading = false;
        s.byId[payload.id] = payload;
      })
      .addCase(getEvidenceThunk.rejected, (s, { payload }) => { s.loading = false; s.error = payload; });

    // LIST
    builder
      .addCase(listEvidenceThunk.pending, (s) => { s.loading = true; s.error = null; })
      .addCase(listEvidenceThunk.fulfilled, (s, { payload }) => {
        s.loading = false;
        const { items } = payload;
        items.forEach(ev => {
          s.byId[ev.id] = ev;
          const pid = ev.post_id;
          if (!s.byPost[pid]) s.byPost[pid] = [];
          if (!s.byPost[pid].some(x => x.id === ev.id)) {
            s.byPost[pid].push(ev);
          }
        });
      })
      .addCase(listEvidenceThunk.rejected, (s, { payload }) => { s.loading = false; s.error = payload; });

    // UPDATE
    builder
      .addCase(updateEvidenceThunk.pending, (s) => { s.loading = true; s.error = null; })
      .addCase(updateEvidenceThunk.fulfilled, (s, { payload }) => {
        s.loading = false;
        s.byId[payload.id] = payload;
        const pid = payload.post_id;
        if (s.byPost[pid]) {
          s.byPost[pid] = s.byPost[pid].map(e => e.id === payload.id ? payload : e);
        }
      })
      .addCase(updateEvidenceThunk.rejected, (s, { payload }) => { s.loading = false; s.error = payload; });

    // DELETE
    builder
      .addCase(deleteEvidenceThunk.pending, (s) => { s.loading = true; s.error = null; })
      .addCase(deleteEvidenceThunk.fulfilled, (s, { payload }) => {
        s.loading = false;
        const { evidenceId } = payload;
        delete s.byId[evidenceId];
        Object.keys(s.byPost).forEach(postId => {
          s.byPost[postId] = s.byPost[postId].filter(ev => ev.id !== evidenceId);
        });
      })
      .addCase(deleteEvidenceThunk.rejected, (s, { payload }) => { s.loading = false; s.error = payload; });

    // FLAG
    builder
      .addCase(flagEvidenceThunk.pending, (s) => { s.loading = true; s.error = null; })
      .addCase(flagEvidenceThunk.fulfilled, (s, { payload }) => {
        s.loading = false;
        const eid = payload.evidence_id;
        if (!s.flagsByEvidence[eid]) s.flagsByEvidence[eid] = [];
        s.flagsByEvidence[eid].push(payload);
      })
      .addCase(flagEvidenceThunk.rejected, (s, { payload }) => { s.loading = false; s.error = payload; });

    builder
      .addCase(getEvidenceFlagsThunk.pending, (s) => { s.loading = true; s.error = null; })
      .addCase(getEvidenceFlagsThunk.fulfilled, (s, { payload }) => {
        s.loading = false;
        const flags = payload;
        if (flags.length > 0) {
          const eid = flags[0].evidence_id;
          s.flagsByEvidence[eid] = flags;
        }
      })
      .addCase(getEvidenceFlagsThunk.rejected, (s, { payload }) => { s.loading = false; s.error = payload; });

    // Admin: resolve a flag. Silent failure here is the worst instance of
    // this bug shape in the file -- a moderator who clicks "resolve" and
    // sees no error has every reason to believe it worked.
    builder
      .addCase(resolveFlagThunk.pending, (s) => { s.loading = true; s.error = null; })
      .addCase(resolveFlagThunk.fulfilled, (s, { payload }) => {
        s.loading = false;
        const eid = payload.evidence_id;
        if (!s.flagsByEvidence[eid]) return;
        s.flagsByEvidence[eid] = s.flagsByEvidence[eid].map(f =>
          f.id === payload.id ? payload : f
        );
      })
      .addCase(resolveFlagThunk.rejected, (s, { payload }) => { s.loading = false; s.error = payload; });

    // HELPFUL votes
    builder
      .addCase(toggleHelpfulThunk.pending, (s) => { s.loading = true; s.error = null; })
      .addCase(toggleHelpfulThunk.fulfilled, (s, { payload }) => {
        s.loading = false;
        const { evidence_id, is_helpful, helpful_count } = payload;
        s.helpful[evidence_id] = { hasVoted: is_helpful, count: helpful_count };
      })
      .addCase(toggleHelpfulThunk.rejected, (s, { payload }) => { s.loading = false; s.error = payload; });

    builder
      .addCase(checkHelpfulThunk.pending, (s) => { s.loading = true; s.error = null; })
      .addCase(checkHelpfulThunk.fulfilled, (s, { payload }) => {
        s.loading = false;
        const { evidence_id, has_voted } = payload;
        if (!s.helpful[evidence_id]) s.helpful[evidence_id] = {};
        s.helpful[evidence_id].hasVoted = has_voted;
      })
      .addCase(checkHelpfulThunk.rejected, (s, { payload }) => { s.loading = false; s.error = payload; });

    // POST SCORE
    builder
      .addCase(getPostScoreThunk.pending, (s) => { s.loading = true; s.error = null; })
      .addCase(getPostScoreThunk.fulfilled, (s, { payload }) => {
        s.loading = false;
        s.postScores[payload.post_id] = payload.score;
      })
      .addCase(getPostScoreThunk.rejected, (s, { payload }) => { s.loading = false; s.error = payload; });

    // USER STATS
    builder
      .addCase(getUserEvidenceStatsThunk.pending, (s) => { s.loading = true; s.error = null; })
      .addCase(getUserEvidenceStatsThunk.fulfilled, (s, { payload }) => {
        s.loading = false;
        s.userStats[payload.user_id] = payload;
      })
      .addCase(getUserEvidenceStatsThunk.rejected, (s, { payload }) => { s.loading = false; s.error = payload; });

    // POST SUMMARY
    builder
      .addCase(getPostEvidenceSummaryThunk.pending, (s) => { s.loading = true; s.error = null; })
      .addCase(getPostEvidenceSummaryThunk.fulfilled, (s, { payload }) => {
        s.loading = false;
        const pid = payload.post_id;
        s.summaries[pid] = payload;
      })
      .addCase(getPostEvidenceSummaryThunk.rejected, (s, { payload }) => { s.loading = false; s.error = payload; });

    // ADMIN disputed list
    builder
      .addCase(getDisputedEvidenceThunk.pending, (s) => { s.loading = true; s.error = null; })
      .addCase(getDisputedEvidenceThunk.fulfilled, (s, { payload }) => {
        s.loading = false;
        s.disputed = payload.items || [];
      })
      .addCase(getDisputedEvidenceThunk.rejected, (s, { payload }) => { s.loading = false; s.error = payload; });

    // Admin: update a disputed evidence item's status. Same silent-failure
    // risk as resolveFlagThunk above -- a moderator needs to know if this
    // didn't actually go through.
    builder
      .addCase(adminUpdateEvidenceStatusThunk.pending, (s) => { s.loading = true; s.error = null; })
      .addCase(adminUpdateEvidenceStatusThunk.fulfilled, (s, { payload }) => {
        s.loading = false;
        const idx = s.disputed.findIndex(e => e.id === payload.id);
        if (idx !== -1) s.disputed[idx] = payload;
      })
      .addCase(adminUpdateEvidenceStatusThunk.rejected, (s, { payload }) => { s.loading = false; s.error = payload; });
  },
});

export const { resetEvidenceState } = slice.actions;
export default slice.reducer;
